import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import {
  ElderShellSlotDirective,
  IframeHostComponent,
  IFrameParams,
  UrlBuilder,
} from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { truthyPredicate } from '../../../../../functional.util'
import { environment } from '../../../../../environments/environment'
import { StoragePath } from '../../../storage/storage-path'
import { ControlTerminal } from '../../control-terminal'

@Component({
  selector: 'app-storage-terminal',
  templateUrl: './storage-terminal.component.html',
  styleUrls: ['./storage-terminal.component.scss'],
  imports: [ElderShellSlotDirective, NgTemplateOutlet, IframeHostComponent, AsyncPipe],
})
export class StorageTerminalComponent implements OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public readonly controlTerminal$ = new BehaviorSubject<ControlTerminal | null>(null)
  public readonly storageScope$ = new BehaviorSubject<StoragePath | null>(null)

  public readonly iframeParams$: Observable<IFrameParams>

  public readonly destroy$ = new Subject<void>()

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const route = inject(ActivatedRoute)
    const ct = route?.parent?.snapshot?.data['controlTerminal'] as ControlTerminal
    if (ct) {
      this.controlTerminal = ct
    }

    this.iframeParams$ = this.storageScope$.pipe(
      filter(truthyPredicate),
      map((storageScope) => this.buildIframeParam(storageScope))
    )
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycles                                                             *
   *                                                                         *
   **************************************************************************/

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set controlTerminal(terminal: ControlTerminal) {
    this.controlTerminal$.next(terminal)

    const storageScope = terminal.storage.storageScope
      ? StoragePath.parse(terminal.storage.storageScope)
      : StoragePath.parse(terminal.facility)

    this.storageScope$.next(storageScope)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private buildIframeParam(storageScope: StoragePath): IFrameParams {
    return {
      srcUrl: this.buildSourceUrl(storageScope),
      hideActionBar: true,
    }
  }

  private buildSourceUrl(storageScope: StoragePath): string {
    return UrlBuilder.parse(environment.storageTerminalUrl)
      .setParam('storageScope', storageScope.serialize())
      .setParam('kioskMode', String(true))
      .build()
  }
}
